import { useCallback } from 'react';
import { EadNode } from 'types';
import { useEadTreeContext } from './EadTreeCtx';
import { listRef } from './VirtualTree';
import { fetchEadChildren } from './fetch-with-cache';
import { getNodeId } from './ctx-utils';

export const useBulkExpand = () => {
  const ectx = useEadTreeContext();
  const {
    setChildrenViewHeap,
    setExpandedNodesIdsHEAP,
    currentArchivalAid,
    nextIdsToExpand,
    setNextIdsToExpand,
    currentEadNode,
    setLoading,
  } = ectx;

  const handleExpandMode = useCallback(async () => {
    setLoading?.(true);
    if (!currentEadNode) {
      console.log('No current ead node');
      setLoading?.(false);
      return;
    }

    const currentNode = currentEadNode;

    if (
      (nextIdsToExpand?.length ?? 0) === 1 &&
      nextIdsToExpand?.[0] === (getNodeId(currentNode) ?? '')
    ) {
      const nodeId = getNodeId(currentNode);
      setExpandedNodesIdsHEAP?.((p) => {
        if (nodeId && p[nodeId]) return p;
        if (nodeId) {
          return { ...p, [nodeId]: true };
        }
        return p;
      });

      setNextIdsToExpand?.(
        currentNode?.children
          ?.filter((c) => c.childrenCount > 0)
          ?.map?.((c) => getNodeId(c)!) ?? []
      );
      listRef.current?.forceUpdate();
      listRef.current?.recomputeRowHeights();
    } else {
      const nodeWithChildren = await fetchEadChildren(nextIdsToExpand ?? []);
      const formattedHeap: Record<string, EadNode[]> = {};
      nodeWithChildren.forEach((n) => {
        const nodeId = getNodeId(n);
        if (nodeId) {
          formattedHeap[nodeId] = n?.children ?? [];
        }
      });
      setChildrenViewHeap?.((p) => ({
        ...p,
        ...formattedHeap,
      }));

      setExpandedNodesIdsHEAP?.((p) => {
        if (!nextIdsToExpand || nextIdsToExpand.length === 0) {
          return p;
        } else {
          const newHeap: Record<string, boolean> = {};
          nextIdsToExpand.forEach((id) => (newHeap[id] = true));
          return { ...p, ...newHeap };
        }
      });

      const expandMore = nodeWithChildren
        .map((c) =>
          (c.children ?? [])
            ?.filter((cc) => cc.childrenCount > 0)
            .map((cc) => getNodeId(cc) ?? '')
        )
        .flat();

      setNextIdsToExpand?.(expandMore);
      listRef.current?.forceUpdate();
      listRef.current?.recomputeRowHeights();
    }
    setLoading?.(false);
  }, [
    currentEadNode,
    nextIdsToExpand,
    setChildrenViewHeap,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
    setLoading,
  ]);

  const handleCollapseAllNodes = useCallback(async () => {
    setExpandedNodesIdsHEAP?.({});

    setNextIdsToExpand?.([
      ...(currentArchivalAid?.eadNode?.children ?? []).map(
        (c) => getNodeId(c)!
      ),
    ]);

    listRef.current?.forceUpdate();
    listRef.current?.recomputeRowHeights();
  }, [currentArchivalAid, setExpandedNodesIdsHEAP, setNextIdsToExpand]);

  return { handleExpandMode, handleCollapseAllNodes };
};
